import React, { useState, useEffect } from 'react'
import { createRequestWithNotify } from '../lib/api'

const TYPE_LABELS = {
  ticket:  '✈️ Авиабилет',
  bus:     '🚌 Автобусный билет',
  evisa:   '🌐 E-виза',
  visa:    '📄 Виза',
  hotel:   '🏨 Отель',
  expense: '💸 Финансовый расход',
  free:    '📝 Свободная заявка',
  sos:     '🆘 Экстренное сообщение',
}

const BLOCKCHAINS = ['TRC20 (TRON)', 'ERC20 (Ethereum)', 'BEP20 (BSC)', 'TON', 'Другая']

const AGENT_CRYPTO = {
  'BOUSSOU CLEMENT GNOMAN': { address: 'TTPcYc83LVmg8FGdCVBy3YmLWFpD9JMYjU', blockchain: 'TRC20 (TRON)' },
  'Mhone Billy':            { address: 'TMxQ8LHcT9SPt4iqjSWqqfjbENzGf1EBY3', blockchain: 'TRC20 (TRON)' },
  'Faude':                  { address: 'THAyR2xAeGZga5yd59P7YDoiNSd8wvtMV7', blockchain: 'TRC20 (TRON)' },
}

const API_BASE = '/api'

export default function CreateRequestModal({ onClose, currentUserId, currentUserName, candidates, darkMode }) {
  const [step, setStep] = useState('type') // type | candidate | fields | confirm
  const [type, setType] = useState(null)
  const [candidate, setCandidate] = useState(null)  // { id, name }
  const [forAgent, setForAgent] = useState(false)   // режим "для агента"
  const [agent, setAgent] = useState(null)          // { id, name } выбранный агент
  const [fields, setFields] = useState({})
  const [agents, setAgents] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [candidateSearch, setCandidateSearch] = useState('')
  const [agentSearch, setAgentSearch] = useState('')

  const myCandidates = (candidates || []).filter(c => !c.archived && !c.moscow_completed)

  // Загружаем агентов при входе на шаг выбора (нужны для агентского режима)
  useEffect(() => {
    if (step === 'candidate') {
      fetch(`${API_BASE}/bot/agents`).then(r => r.json()).then(setAgents).catch(() => setAgents([]))
    }
  }, [step])

  const bg = darkMode ? '#1a1f2e' : 'white'
  const textColor = darkMode ? '#e2e8f0' : '#1a1a1a'
  const borderColor = darkMode ? '#2d3448' : '#e8e8e5'
  const inputStyle = { padding: '8px 12px', borderRadius: 8, border: `1px solid ${borderColor}`, fontSize: 13, width: '100%', boxSizing: 'border-box', background: darkMode ? '#252b3b' : 'white', color: textColor }
  const labelStyle = { fontSize: 12, color: '#888', marginBottom: 4, display: 'block' }

  function needsCandidate(t) { return ['ticket','bus','evisa','visa','hotel','expense'].includes(t) }

  function selectType(t) {
    setType(t)
    setFields({})
    setCandidateSearch('')
    setAgentSearch('')
    setForAgent(false)
    setAgent(null)
    setCandidate(null)
    if (needsCandidate(t)) {
      setStep('candidate')
    } else {
      setStep('fields')
    }
  }

  function switchMode(isAgent) {
    setForAgent(isAgent)
    setAgent(null)
    setCandidate(null)
    setCandidateSearch('')
    setAgentSearch('')
  }

  function selectAgent(ag) {
    const crypto = AGENT_CRYPTO[ag.name] || {}
    setAgent({ id: ag.id, name: ag.name })
    // Для expense автозаполняем крипто из справочника
    if (type === 'expense') {
      setFields(prev => ({
        ...prev,
        recipient_type: 'agent',
        agent_id: String(ag.id),
        agent_name: ag.name,
        crypto_address: crypto.address || '',
        blockchain: crypto.blockchain || '',
      }))
    }
    setStep('fields')
  }

  async function handleSubmit() {
    setSaving(true)
    setError(null)
    try {
      const data = { ...fields }
      if (candidate) {
        data.candidate_name = candidate.name
        data.candidate_id_ref = candidate.id
      }
      if (forAgent && agent) {
        data.for_agent = true
        data.agent_name = agent.name
        data.agent_id_ref = String(agent.id)
        // Для expense recipient_type уже в fields
        if (type !== 'expense') {
          data.recipient_type = 'agent'
        }
      }
      await createRequestWithNotify({
        type,
        operator_id: currentUserId,
        operator_name: currentUserName,
        candidate_id: candidate?.id || null,
        data,
      })
      onClose(true)
    } catch (e) {
      setError('Ошибка при отправке: ' + e.message)
    }
    setSaving(false)
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }
  const modal = { background: bg, borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', padding: 24, color: textColor }

  // ── Step: choose type ──────────────────────────────────────────────────────
  if (step === 'type') return (
    <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>Новая заявка</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: '#888', marginBottom: 16 }}>Выберите тип заявки:</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {Object.entries(TYPE_LABELS).map(([t, label]) => (
            <button key={t} onClick={() => selectType(t)} style={{ padding: '12px 16px', borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? '#252b3b' : '#f9f9f7', color: textColor, fontSize: 14, textAlign: 'left', cursor: 'pointer', fontWeight: 500 }}>
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  )

  // ── Step: choose candidate or agent ───────────────────────────────────────
  if (step === 'candidate') {
    const toggleStyle = (active) => ({
      flex: 1, padding: '8px 0', borderRadius: 8, fontSize: 13, cursor: 'pointer', fontWeight: active ? 600 : 400,
      border: `1px solid ${active ? '#1a1a1a' : borderColor}`,
      background: active ? '#1a1a1a' : (darkMode ? '#252b3b' : 'white'),
      color: active ? 'white' : textColor,
    })

    return (
      <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={modal}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <button onClick={() => setStep('type')} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 13, padding: 0, marginRight: 8 }}>← назад</button>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{TYPE_LABELS[type]}</span>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
          </div>

          {/* Переключатель Кандидат / Агент */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <button style={toggleStyle(!forAgent)} onClick={() => switchMode(false)}>👤 Кандидат</button>
            <button style={toggleStyle(forAgent)} onClick={() => switchMode(true)}>🏦 Агент</button>
          </div>

          {!forAgent ? (
            <>
              <input
                autoFocus
                value={candidateSearch}
                onChange={e => setCandidateSearch(e.target.value)}
                placeholder="🔍 Поиск по имени..."
                style={{ ...inputStyle, marginBottom: 12 }}
              />
              {myCandidates.length === 0 ? (
                <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: 20 }}>Нет активных кандидатов</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
                  {myCandidates.filter(c => !candidateSearch || c.name?.toLowerCase().includes(candidateSearch.toLowerCase())).map(c => (
                    <button key={c.id} onClick={() => { setCandidate({ id: c.id, name: c.name }); setStep('fields') }}
                      style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? '#252b3b' : '#f9f9f7', color: textColor, fontSize: 13, textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                      {c.photo_url && <img src={c.photo_url} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />}
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        {c.status && <div style={{ fontSize: 11, color: '#888' }}>{c.status}</div>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <input
                autoFocus
                value={agentSearch}
                onChange={e => setAgentSearch(e.target.value)}
                placeholder="🔍 Поиск агента..."
                style={{ ...inputStyle, marginBottom: 12 }}
              />
              {agents.length === 0 ? (
                <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: 20 }}>Загрузка агентов...</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 340, overflowY: 'auto' }}>
                  {agents.filter(a => !agentSearch || a.name?.toLowerCase().includes(agentSearch.toLowerCase())).map(a => (
                    <button key={a.id} onClick={() => selectAgent(a)}
                      style={{ padding: '10px 14px', borderRadius: 10, border: `1px solid ${borderColor}`, background: darkMode ? '#252b3b' : '#f9f9f7', color: textColor, fontSize: 13, textAlign: 'left', cursor: 'pointer' }}>
                      <div style={{ fontWeight: 600 }}>{a.name}</div>
                      {AGENT_CRYPTO[a.name] && <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>💎 {AGENT_CRYPTO[a.name].blockchain}</div>}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    )
  }

  // ── Step: fill fields ──────────────────────────────────────────────────────
  if (step === 'fields') {
    function f(key) { return fields[key] || '' }
    function sf(key) { return v => setFields(prev => ({ ...prev, [key]: v })) }

    let fieldBlock = null

    if (type === 'ticket') fieldBlock = (
      <>
        <label style={labelStyle}>Откуда</label>
        <input style={inputStyle} value={f('from')} onChange={e => sf('from')(e.target.value)} placeholder="Город или аэропорт" />
        <label style={{ ...labelStyle, marginTop: 12 }}>Куда</label>
        <input style={inputStyle} value={f('to')} onChange={e => sf('to')(e.target.value)} placeholder="Город или аэропорт" />
        <label style={{ ...labelStyle, marginTop: 12 }}>Дата вылета</label>
        <input style={inputStyle} type="date" value={f('departure_date')} onChange={e => sf('departure_date')(e.target.value)} />
        <label style={{ ...labelStyle, marginTop: 12 }}>Время вылета (местное, необязательно)</label>
        <input style={inputStyle} value={f('departure_time')} onChange={e => sf('departure_time')(e.target.value)} placeholder="13:45" />
        <label style={{ ...labelStyle, marginTop: 12 }}>Комментарий (необязательно)</label>
        <input style={inputStyle} value={f('comment')} onChange={e => sf('comment')(e.target.value)} placeholder="" />
      </>
    )

    if (type === 'bus') fieldBlock = (
      <>
        <label style={labelStyle}>Откуда</label>
        <input style={inputStyle} value={f('from')} onChange={e => sf('from')(e.target.value)} placeholder="Город или автовокзал" />
        <label style={{ ...labelStyle, marginTop: 12 }}>Куда</label>
        <input style={inputStyle} value={f('to')} onChange={e => sf('to')(e.target.value)} placeholder="Город или автовокзал" />
        <label style={{ ...labelStyle, marginTop: 12 }}>Дата отправления</label>
        <input style={inputStyle} type="date" value={f('departure_date')} onChange={e => sf('departure_date')(e.target.value)} />
        <label style={{ ...labelStyle, marginTop: 12 }}>Время отправления (необязательно)</label>
        <input style={inputStyle} value={f('departure_time')} onChange={e => sf('departure_time')(e.target.value)} placeholder="08:00" />
        <label style={{ ...labelStyle, marginTop: 12 }}>Комментарий (необязательно)</label>
        <input style={inputStyle} value={f('comment')} onChange={e => sf('comment')(e.target.value)} placeholder="" />
      </>
    )

    if (type === 'evisa') fieldBlock = (
      <>
        <label style={labelStyle}>Страна</label>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          {['Россия', 'Эфиопия', 'Другая'].map(c => (
            <button key={c} onClick={() => sf('country')(c)} style={{ flex: 1, padding: '8px 0', borderRadius: 8, border: `1px solid ${f('country') === c ? '#1a1a1a' : borderColor}`, background: f('country') === c ? '#1a1a1a' : (darkMode ? '#252b3b' : 'white'), color: f('country') === c ? 'white' : textColor, fontSize: 13, cursor: 'pointer' }}>{c}</button>
          ))}
        </div>
        {f('country') === 'Другая' && (
          <>
            <label style={labelStyle}>Укажите страну</label>
            <input style={{ ...inputStyle, marginBottom: 12 }} value={f('country_other')} onChange={e => sf('country_other')(e.target.value)} />
          </>
        )}
        <label style={labelStyle}>Дата въезда</label>
        <input style={inputStyle} type="date" value={f('entry_date')} onChange={e => sf('entry_date')(e.target.value)} />
      </>
    )

    if (type === 'visa') fieldBlock = (
      <>
        <label style={labelStyle}>Страна / Посольство</label>
        <input style={inputStyle} value={f('country')} onChange={e => sf('country')(e.target.value)} placeholder="Например: Германия" />
        <label style={{ ...labelStyle, marginTop: 12 }}>Дата подачи</label>
        <input style={inputStyle} type="date" value={f('submission_date')} onChange={e => sf('submission_date')(e.target.value)} />
      </>
    )

    if (type === 'hotel') fieldBlock = (
      <>
        <label style={labelStyle}>Город / Локация</label>
        <input style={inputStyle} value={f('city')} onChange={e => sf('city')(e.target.value)} placeholder="Город" />
        <label style={{ ...labelStyle, marginTop: 12 }}>Дата заезда</label>
        <input style={inputStyle} type="date" value={f('check_in')} onChange={e => sf('check_in')(e.target.value)} />
        <label style={{ ...labelStyle, marginTop: 12 }}>Дата выезда</label>
        <input style={inputStyle} type="date" value={f('check_out')} onChange={e => sf('check_out')(e.target.value)} />
        <label style={{ ...labelStyle, marginTop: 12 }}>Количество гостей</label>
        <input style={inputStyle} type="number" min="1" value={f('guests') || 1} onChange={e => sf('guests')(e.target.value)} />
      </>
    )

    if (type === 'expense') {
      const recipientType = f('recipient_type') || 'agent'
      fieldBlock = (
        <>
          <label style={labelStyle}>Сумма (USD)</label>
          <input style={inputStyle} type="number" value={f('amount_usd')} onChange={e => sf('amount_usd')(e.target.value)} placeholder="200" />
          <label style={{ ...labelStyle, marginTop: 12 }}>Цель перевода (необязательно)</label>
          <input style={inputStyle} value={f('comment')} onChange={e => sf('comment')(e.target.value)} placeholder="Пропустить" />

          {/* Переключатель "кому" только если не выбран агент на предыдущем шаге */}
          {!forAgent && (
            <>
              <label style={{ ...labelStyle, marginTop: 12 }}>Кому перевод</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
                {[['agent', '🏦 Агенту'], ['candidate', '🙋 Кандидату напрямую']].map(([val, label]) => (
                  <button key={val} onClick={() => setFields(prev => ({ ...prev, recipient_type: val, agent_id: '', agent_name: '', crypto_address: '', blockchain: '' }))}
                    style={{ flex: 1, padding: '9px 0', borderRadius: 8, border: `1px solid ${recipientType === val ? '#1a1a1a' : borderColor}`, background: recipientType === val ? '#1a1a1a' : (darkMode ? '#252b3b' : 'white'), color: recipientType === val ? 'white' : textColor, fontSize: 13, cursor: 'pointer', fontWeight: recipientType === val ? 600 : 400 }}>
                    {label}
                  </button>
                ))}
              </div>

              {recipientType === 'agent' && (
                <>
                  <label style={{ ...labelStyle, marginTop: 12 }}>Агент</label>
                  <select style={inputStyle} value={f('agent_id')} onChange={e => {
                    const ag = agents.find(a => String(a.id) === e.target.value)
                    const crypto = ag ? (AGENT_CRYPTO[ag.name] || {}) : {}
                    setFields(prev => ({ ...prev, agent_id: e.target.value, agent_name: ag?.name || '', crypto_address: crypto.address || '', blockchain: crypto.blockchain || '' }))
                  }}>
                    <option value="">Выберите агента</option>
                    {agents.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </>
              )}
            </>
          )}

          <label style={{ ...labelStyle, marginTop: 12 }}>Крипто-адрес</label>
          <input style={inputStyle} value={f('crypto_address')} onChange={e => sf('crypto_address')(e.target.value)} placeholder="Адрес кошелька" />
          <label style={{ ...labelStyle, marginTop: 12 }}>Блокчейн (сеть)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {BLOCKCHAINS.map(b => (
              <button key={b} onClick={() => sf('blockchain')(b)} style={{ padding: '6px 12px', borderRadius: 8, border: `1px solid ${f('blockchain') === b ? '#1a1a1a' : borderColor}`, background: f('blockchain') === b ? '#1a1a1a' : (darkMode ? '#252b3b' : 'white'), color: f('blockchain') === b ? 'white' : textColor, fontSize: 12, cursor: 'pointer' }}>{b}</button>
            ))}
          </div>
        </>
      )
    }

    if (type === 'free') fieldBlock = (
      <>
        <label style={labelStyle}>Тема</label>
        <input style={inputStyle} value={f('subject')} onChange={e => sf('subject')(e.target.value)} placeholder="Краткая тема" />
        <label style={{ ...labelStyle, marginTop: 12 }}>Описание</label>
        <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} value={f('description')} onChange={e => sf('description')(e.target.value)} placeholder="Подробности..." />
      </>
    )

    if (type === 'sos') fieldBlock = (
      <>
        <label style={labelStyle}>Описание ситуации</label>
        <textarea style={{ ...inputStyle, minHeight: 100, resize: 'vertical' }} value={f('text')} onChange={e => sf('text')(e.target.value)} placeholder="Опишите ситуацию..." />
      </>
    )

    function canSubmit() {
      if (type === 'ticket') return f('from') && f('to') && f('departure_date')
      if (type === 'bus') return f('from') && f('to') && f('departure_date')
      if (type === 'evisa') return (f('country') && f('country') !== 'Другая' || f('country_other')) && f('entry_date')
      if (type === 'visa') return f('country') && f('submission_date')
      if (type === 'hotel') return f('city') && f('check_in') && f('check_out')
      if (type === 'expense') {
        if (!f('amount_usd') || !f('crypto_address') || !f('blockchain')) return false
        if (forAgent) return true // агент уже выбран на предыдущем шаге
        const rt = f('recipient_type') || 'agent'
        return rt === 'candidate' || f('agent_id')
      }
      if (type === 'free') return f('subject') && f('description')
      if (type === 'sos') return f('text')
      return false
    }

    return (
      <div style={overlay} onClick={e => e.target === e.currentTarget && onClose()}>
        <div style={modal}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <div>
              <button onClick={() => setStep(needsCandidate(type) ? 'candidate' : 'type')} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', fontSize: 13, padding: 0, marginRight: 8 }}>← назад</button>
              <span style={{ fontWeight: 700, fontSize: 16 }}>{TYPE_LABELS[type]}</span>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
          </div>

          {/* Чип кандидата или агента */}
          {candidate && (
            <div style={{ background: darkMode ? '#252b3b' : '#f5f5f3', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: textColor }}>
              👤 <b>{candidate.name}</b>
            </div>
          )}
          {forAgent && agent && (
            <div style={{ background: darkMode ? '#252b3b' : '#f0f4ff', borderRadius: 8, padding: '8px 12px', marginBottom: 16, fontSize: 13, color: textColor }}>
              🏦 <b>{agent.name}</b>
            </div>
          )}

          {fieldBlock}

          {error && <div style={{ marginTop: 12, color: '#E24B4A', fontSize: 12 }}>{error}</div>}

          <button
            onClick={handleSubmit}
            disabled={saving || !canSubmit()}
            style={{ marginTop: 20, width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: canSubmit() && !saving ? '#1a1a1a' : '#ccc', color: 'white', fontSize: 14, fontWeight: 600, cursor: canSubmit() && !saving ? 'pointer' : 'default' }}>
            {saving ? 'Отправка...' : '📤 Отправить заявку'}
          </button>
        </div>
      </div>
    )
  }

  return null
}
